import { useCallback, useEffect, useMemo, useState } from "react";
import { Typography, useTheme } from "@mui/material";
import { IStorageService } from "../../../modules/storage/models/IStorageService";
import {
  BalanceRequest,
  BalanceList,
  BalanceListRows,
  BalanceRow,
  BalanceSummaryRow,
} from "../../../modules/storage/models/storage-dto";
import AppError from "../../../utils/appError";
import { convertToDefaultDate } from "../../../utils/dateFormatter";
import { formatCNPJ, getBalanceColor, getThousand } from "../../../utils";
import { isEqual } from "lodash";

const ROWS_PER_PAGE = 10;

interface Filters {
  cnpjs: string[];
  contractId: string;
  date: string;
  orderNumber: string;
  orderSeries: string;
}

const initialFilters: Filters = {
  cnpjs: [],
  contractId: "",
  date: "",
  orderNumber: "",
  orderSeries: "",
};

const initialList: BalanceList = {
  rows: [],
  count: 0,
};

export interface BalanceDeps {
  storageService: IStorageService;
  snackbar: (args: { message: string; variant: "success" | "error" }) => void;
}

export interface BalanceProps {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useBalanceStorage(
  { storageService, snackbar }: BalanceDeps,
  { isLoading, setIsLoading }: BalanceProps
) {
  const [tab, setTab] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [balanceList, setBalanceList] = useState<BalanceList>(initialList);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(initialFilters);

  const theme = useTheme();

  const fetchList = useCallback(
    async (newFilters = appliedFilters) => {
      try {
        setIsLoading(true);
        const { contractId, orderNumber, orderSeries } = newFilters;

        const newData: BalanceRequest = {
          contractId: contractId,
          orderNumber: orderNumber,
          orderSeries: orderSeries,
          page: page,
          limit: ROWS_PER_PAGE,
        };

        if (tab) {
          const response = await storageService.getStorageBalance(newData);
          const isArray = Array.isArray(response);
          const content = isArray ? response : response?.content;
          const total = isArray ? response.length : response?.total;

          setBalanceList({
            count: total || 0,
            rows: Array.isArray(content) ? content.map(formatBalanceList) : [],
          });

          return;
        }

        const response = await storageService.getStorageBalanceSummary(newData);
        const isArray = Array.isArray(response);
        const content = isArray ? response : response?.content;
        const total = isArray ? response.length : response?.total;

        setBalanceList({
          count: total || 0,
          rows: Array.isArray(content)
            ? content.map(formatBalanceSummaryList)
            : [],
        });
      } catch (error) {
        snackbar({
          message: (error as AppError).message,
          variant: "error",
        });

        setBalanceList(initialList);
      } finally {
        setIsLoading(false);
      }
    },
    [appliedFilters, page, tab]
  );

  const formatBalanceList = (data: BalanceRow): BalanceListRows => ({
    CNPJ: formatCNPJ(data.CNPJ),
    invoice: data.invoice,
    salesOrder: data.salesOrder || "-",
    nf: data.nf,
    series: data.series,
    value: (
      <Typography color={colorizeAndTranslateByStatus(data.type).color}>
        {`${data.type === "ENTRANCE" ? "+" : "-"}${getThousand(
          Number(data.value)
        )}`}
      </Typography>
    ),
    nfDate: data.nfDate ? convertToDefaultDate(data.nfDate) : "-",
    orderDate: data.orderDate ?? "-",
    type: colorizeAndTranslateByStatus(data.type).translate,
    remainingQuotaBalance: getThousand(Number(data.remainingQuotaBalance)),
  });

  const formatBalanceSummaryList = (
    data: BalanceSummaryRow
  ): BalanceListRows => {
    const balanceValue = Number(data.balance ?? 0);

    return {
      customerCompany: data.customerCompany?.description || "-",
      CNPJ: formatCNPJ(data.customerCompany?.document ?? ""),
      product: data.product || "-",
      mod: data.mod || "-",
      quota: getThousand(Number(data.quota ?? 0)),
      finalQuota: getThousand(Number(data.finalQuota ?? 0)),
      added: getThousand(Number(data.additional ?? 0)),
      cut: getThousand(Number(data.cut ?? 0)),
      stated: getThousand(Number(data.stated ?? 0)),
      balance: (
        <Typography color={getBalanceColor(balanceValue)}>
          {getThousand(balanceValue)}
        </Typography>
      ),
    };
  };

  const colorizeAndTranslateByStatus = (operation: string) => {
    const { success, error, shadesOfDark } = theme.palette;
    switch (operation) {
      case "ENTRANCE":
        return { color: success.light, translate: "Entrada" };
      case "RETRIEVAL":
        return { color: error.light, translate: "Saída" };
      default:
        return { color: shadesOfDark.dark, translate: "Desconhecido" };
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleTabChange = (newTab: number) => {
    setTab(newTab);
    setPage(1);
  };

  const handleApplyFilters = (newFilters: Filters) => {
    setPage(1);
    setAppliedFilters(newFilters);
    fetchList(newFilters);
  };

  const handleClearList = () => {
    setPage(1);
    setAppliedFilters(initialFilters);
    setBalanceList(initialList);
  };

  useEffect(() => {
    if (!appliedFilters.contractId) return;
    fetchList();
  }, [page]);

  const hasAppliedFilters = useMemo(() => {
    return !isEqual(appliedFilters, initialFilters);
  }, [appliedFilters, initialFilters]);

  return {
    tab,
    page,
    hasAppliedFilters,

    balanceList,
    setBalanceList,
    appliedFilters,
    setAppliedFilters,

    handleApplyFilters,
    handlePageChange,
    handleTabChange,
    fetchList,
    handleClearList,
  };
}
